/** 原创静态图片滤镜；不包含第三方付费 LUT 或素材。 */
export const IMAGE_FILTERS = [
  {
    id: "softRoom",
    category: "indoor",
    warm: 0.08,
    saturation: 0.95,
    contrast: 0.94,
    exposure: 0.05,
    fade: 0.02,
  },
  {
    id: "amber",
    category: "autumn",
    warm: 0.18,
    saturation: 1.12,
    contrast: 1.04,
    exposure: 0,
    fade: 0.02,
  },
  {
    id: "portrait",
    category: "portrait",
    warm: 0.04,
    saturation: 0.94,
    contrast: 0.95,
    exposure: 0.06,
    fade: 0.01,
  },
  {
    id: "landscape",
    category: "landscape",
    warm: -0.03,
    saturation: 1.25,
    contrast: 1.12,
    exposure: 0.02,
    fade: 0,
  },
  {
    id: "food",
    category: "food",
    warm: 0.1,
    saturation: 1.2,
    contrast: 1.05,
    exposure: 0.05,
    fade: 0,
  },
  {
    id: "camera",
    category: "camera",
    warm: 0.02,
    saturation: 0.9,
    contrast: 1.14,
    exposure: 0,
    fade: 0.025,
  },
  {
    id: "clear",
    category: "clear",
    warm: 0,
    saturation: 1.06,
    contrast: 1.08,
    exposure: 0.04,
    fade: 0,
  },
  {
    id: "night",
    category: "night",
    warm: -0.14,
    saturation: 0.86,
    contrast: 1.16,
    exposure: 0.05,
    fade: 0.02,
  },
  {
    id: "cinema",
    category: "cinema",
    warm: -0.07,
    saturation: 0.78,
    contrast: 1.15,
    exposure: -0.02,
    fade: 0.035,
  },
  {
    id: "outdoor",
    category: "outdoor",
    warm: 0.05,
    saturation: 1.16,
    contrast: 1.06,
    exposure: 0.02,
    fade: 0,
  },
  {
    id: "stylized",
    category: "stylized",
    warm: -0.2,
    saturation: 1.35,
    contrast: 1.18,
    exposure: 0,
    fade: 0.03,
  },
  {
    id: "mono",
    category: "monochrome",
    warm: 0,
    saturation: 0,
    contrast: 1.19,
    exposure: 0.025,
    fade: 0.01,
  },
  {
    id: "silver",
    category: "monochrome",
    warm: 0,
    saturation: 0,
    contrast: 0.87,
    exposure: 0.04,
    fade: 0.025,
  },
  {
    id: "film",
    category: "film",
    warm: 0.1,
    saturation: 0.76,
    contrast: 0.91,
    exposure: 0,
    fade: 0.065,
  },
  {
    id: "coolFilm",
    category: "film",
    warm: -0.08,
    saturation: 0.84,
    contrast: 0.96,
    exposure: 0.01,
    fade: 0.045,
  },
  {
    id: "basic",
    category: "basic",
    warm: 0,
    saturation: 1,
    contrast: 1.06,
    exposure: 0.02,
    fade: 0,
  },
] as const;
export type ImageFilterId = (typeof IMAGE_FILTERS)[number]["id"];
export interface ImageFilter {
  id?: ImageFilterId | "none";
  strength?: number;
  enabled?: boolean;
}
export interface NormalizedImageFilter {
  id: ImageFilterId | "none";
  strength: number;
  enabled: boolean;
}
export function normalizeImageFilter(
  input?: ImageFilter | null,
): NormalizedImageFilter {
  return {
    id: IMAGE_FILTERS.some((f) => f.id === input?.id) ? input!.id! : "none",
    strength: Number.isFinite(input?.strength)
      ? Math.max(0, Math.min(100, input!.strength!))
      : 100,
    enabled: input?.enabled !== false,
  };
}
export function hasImageFilter(input?: ImageFilter | null) {
  const f = normalizeImageFilter(input);
  return f.enabled && f.id !== "none" && f.strength > 0;
}
export function applyImageFilter(image: ImageData, input?: ImageFilter | null) {
  const config = normalizeImageFilter(input),
    filter = IMAGE_FILTERS.find((f) => f.id === config.id);
  if (!filter || !hasImageFilter(config)) return image;
  const d = image.data,
    a = config.strength / 100,
    gain = 2 ** filter.exposure * filter.contrast * (1 - filter.fade * 2),
    sat = filter.saturation,
    base = filter.fade + (1 - filter.fade * 2) * (0.5 - 0.5 * filter.contrast),
    warm = filter.warm * 0.18 * (1 - filter.fade * 2),
    clamp = (n: number) => Math.max(0, Math.min(1, n));
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3] === 0) continue;
    const rr = d[i],
      gg = d[i + 1],
      bb = d[i + 2],
      r = rr / 255,
      g = gg / 255,
      b = bb / 255,
      y = (r * 0.2126 + g * 0.7152 + b * 0.0722) * (1 - sat) * gain;
    d[i] = Math.round(
      rr * (1 - a) + clamp(r * sat * gain + y + base + warm) * 255 * a,
    );
    d[i + 1] = Math.round(
      gg * (1 - a) + clamp(g * sat * gain + y + base) * 255 * a,
    );
    d[i + 2] = Math.round(
      bb * (1 - a) + clamp(b * sat * gain + y + base - warm) * 255 * a,
    );
  }
  return image;
}
